import { IApplicationState } from 'components/store/types'
import {
  IClinique,
  ICoreState,
  IGoogleBusinessPeriod,
  IRegion,
  ITreatment,
} from 'components/core/types'
import getZawinCode from 'utils/getZawinCode'
import { IQueryParams } from 'hooks/useQueryParams'

const subState = (state: IApplicationState): ICoreState => state.core

export const getActiveRegions = (state: IApplicationState) => subState(state).activeRegions
export const getActiveView = (state: IApplicationState) => subState(state).activeView
export const getCliniques = (state: IApplicationState) => subState(state).cliniques
export const getCurrentClinique = (state: IApplicationState): IClinique | null => {
  const activeClinique = subState(state).currentClinique
  return subState(state).cliniques.filter((clinique: IClinique) => clinique.id === activeClinique)?.[0]
}
export const getCurrentCliniqueId = (state: IApplicationState): number | null => subState(state).currentClinique
export const getCurrentPersonType = (state: IApplicationState) => subState(state).currentPersonType
export const getCurrentStep = (state: IApplicationState) => subState(state).currentStep
export const getCurrentRegion = (state: IApplicationState) => subState(state).currentRegion
export const getCurrentTreatment = (state: IApplicationState): ITreatment | null => {
  const activeTreatment = subState(state).currentTreatment
  return subState(state).treatments.filter((treatment: ITreatment) => treatment.id === activeTreatment)?.[0]
}
export const getCurrentTreatmentId = (state: IApplicationState): number | null => subState(state).currentTreatment
export const getCurrentZawinCode = (state: IApplicationState): string | null => {
  const currentClinique = getCurrentClinique(state)
  const currentTreatment = getCurrentTreatment(state)
  const currentPersonType = getCurrentPersonType(state)
  return getZawinCode(currentTreatment, currentPersonType, currentClinique)
}
export const getFormSubmittingStatus = (state: IApplicationState) => subState(state).formIsSubmitting
export const getHideUrlStatus = (state: IApplicationState) => subState(state).hideUrl
export const getLocale = (state: IApplicationState) => subState(state).locale
export const getMapActiveMarker = (state: IApplicationState) => subState(state).mapActiveMarker
export const getQueryParams = (state: IApplicationState): IQueryParams => ({
  rafClinique: subState(state).currentClinique?.toString(),
  rafPersonType: subState(state).currentPersonType?.toString(),
  rafRegion: subState(state).currentRegion?.toString(),
  rafStep: subState(state).currentStep?.toString(),
  rafTreatment: subState(state).currentTreatment?.toString(),
})
export const getRegionCliniques = (state: IApplicationState): IClinique[] => {
  const activeRegion = getCurrentRegion(state)
  return subState(state).cliniques
    .sort(({ title: a }: IClinique, { title: b }: IClinique) => a.localeCompare(b))
    .filter((clinique: IClinique) => clinique.region.includes(activeRegion))
}
export const getRegions = (state: IApplicationState) => subState(state).regions
  .sort(({ order: a }: IRegion, { order: b }: IRegion) => a - b)
export const getBackdropIndex = (state: IApplicationState) => subState(state).backdropIndex
export const getTreatments = (state: IApplicationState) => subState(state).treatments
  .sort(({ order: a }: ITreatment, { order: b }: ITreatment) => a - b)
export const getUrgencyLink = (state: IApplicationState) => subState(state).options.urgency_link
export const getZawinLink = (state: IApplicationState) => subState(state).options.zawin_link

const getCliniqueOpeningHoursPeriods = (clinique: IClinique) => clinique.google_datas?.result?.opening_hours?.periods
const getCliniqueCurrentPeriods = (clinique: IClinique) => clinique.google_datas?.result?.current_opening_hours?.periods
const getTodayHourFormatted = (today = new Date()): string => {
  const todayHours = today.getHours().toString().padStart(2, '0')
  const todayMinutes = today.getMinutes().toString().padStart(2, '0')
  return `${todayHours}${todayMinutes}`
}
const isPeriodDayToday = (
  period: IGoogleBusinessPeriod,
  today = new Date(),
): boolean => period.close.day === today.getDay()
const getTodayPeriods = (clinique: IClinique, today = new Date()): IGoogleBusinessPeriod[] => {
  const currentPeriods = getCliniqueCurrentPeriods(clinique)
  if (!currentPeriods) {
    return []
  }
  const currentPeriodsToday = currentPeriods.filter((period: IGoogleBusinessPeriod) => isPeriodDayToday(period, today))
  if (currentPeriodsToday.length) {
    return currentPeriodsToday
  }
  const openingHourPeriods = getCliniqueOpeningHoursPeriods(clinique)
  return openingHourPeriods.filter((period: IGoogleBusinessPeriod) => isPeriodDayToday(period, today))
}
const getCurrentPeriod = (clinique: IClinique, today = new Date()): IGoogleBusinessPeriod[] => {
  const periods = getTodayPeriods(clinique, today)
  const nowTime = getTodayHourFormatted(today)
  return !!periods && periods.filter((period: IGoogleBusinessPeriod) => (
    period.close.time > nowTime && period.open.time < nowTime
  ))
}
const isCliniqueOpen = (clinique: IClinique, today = new Date()) => !!getCurrentPeriod(clinique, today).length
const getPeriodOpenMinValue = (previousValue: IGoogleBusinessPeriod, currentValue: IGoogleBusinessPeriod) => (
  previousValue?.open?.time < currentValue?.open?.time ? previousValue : currentValue
)
const getNextCloseHour = (clinique: IClinique, today = new Date()): string => {
  const todayHour = getTodayHourFormatted(today)
  const periods = getTodayPeriods(clinique, today)
    .filter((period: IGoogleBusinessPeriod) => period.close?.time > todayHour)
  return periods.length
    ? periods.reduce(
      (previousValue: IGoogleBusinessPeriod, currentValue: IGoogleBusinessPeriod) => (
        previousValue.open.time > currentValue.open.time ? previousValue : currentValue
      )
    ).open.time
    : null
}
const getNextOpenHour = (clinique: IClinique, today = new Date()) => {
  const todayHour = getTodayHourFormatted(today)
  const todayNextPeriods = getTodayPeriods(clinique, today)
    .filter((period: IGoogleBusinessPeriod) => period.close.time > todayHour)
  if (todayNextPeriods.length) {
    return todayNextPeriods.reduce(getPeriodOpenMinValue, null)?.open?.time
  }
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const nextDayPeriods = getTodayPeriods(clinique, tomorrow)
  if (nextDayPeriods.length) {
    const nextDayOpenTime = nextDayPeriods.reduce(getPeriodOpenMinValue, null)?.open?.time
    return parseInt(nextDayOpenTime, 10) < parseInt(getTodayHourFormatted(today), 10) ? nextDayOpenTime : null
  }
  return null
}
const getNextStatusChangeHour = (clinique: IClinique, today = new Date()): null | string => {
  const todayHourFormatted = parseInt(getTodayHourFormatted(today), 10)
  const isOpen = isCliniqueOpen(clinique, today)
  if (isOpen) {
    const nextCloseHour = getNextCloseHour(clinique, today)
    if (nextCloseHour) {
      const delay = parseInt(nextCloseHour, 10) - todayHourFormatted
      if (delay > 0 && delay < 100) {
        return nextCloseHour
      }
    }
    return null
  }
  const nextOpenHour = getNextOpenHour(clinique, today)
  if (nextOpenHour) {
    return nextOpenHour
  }
  return null
}
export const getCliniqueOpenCloseDetails = (clinique: IClinique, today = new Date()) => {
  const isOpen = isCliniqueOpen(clinique, today)
  const nextStatusChangeHour = getNextStatusChangeHour(clinique, today)
  const nextStatus = isOpen ? 'closeAt' : 'openAt'

  const defaultClassName = isOpen ? 'tw-text-light-green' : 'tw-text-orange'
  const updatedClassName = nextStatus === 'openAt' ? 'tw-text-orange' : 'tw-text-light-orange'
  const className = nextStatusChangeHour ? updatedClassName : defaultClassName
  return {
    status: isOpen,
    className,
    nextStatus: nextStatusChangeHour ? nextStatus : null,
    nextStatusChangeHour: nextStatusChangeHour
      ? [nextStatusChangeHour.slice(0, 2), ':', nextStatusChangeHour.slice(2)].join('')
      : null,
  }
}
